const SHORT_ID_PATTERN = /(?:seq|nonseq)_\d+/;

export const extractSeqId = (event) => {
  if (!event) return null;
  if (event.details?.short_id) return event.details.short_id;
  if (event.details?.reason) {
    const match = event.details.reason.match(/Reviewing ((?:seq|nonseq)_\d+)/);
    if (match) return match[1];
  }
  if (event.title) {
    const match = event.title.match(SHORT_ID_PATTERN);
    if (match) return match[0];
  }
  return null;
};

const getIterationNumber = (event) => (
  event?.iteration ?? event?.details?.iteration ?? null
);

const getIterationForEvent = (event, byStepId) => {
  let iteration = getIterationNumber(event);

  if (event?.parent_id) {
    const parent = byStepId[event.parent_id];
    if (parent?.event_type === "iteration_start") {
      iteration = getIterationNumber(parent);
    }
  }

  if (iteration == null && event?.details?.iteration != null) {
    iteration = event.details.iteration;
  }

  return iteration;
};

const blockKey = (seqId, iteration) => `${seqId}::${iteration ?? "unknown"}`;

export const getSequenceBlockSummary = (sequenceBlock) => {
  const scores = sequenceBlock.events
    .filter((event) => typeof event.details?.score === "number")
    .map((event) => event.details.score);

  return {
    lastScore: scores.length > 0 ? scores[scores.length - 1] : null,
    eventCount: sequenceBlock.events.length,
  };
};

/**
 * Builds a chronological timeline — pipeline events and sequence blocks appear
 * in the same order as the source events array (no reordering).
 */
export const buildExecutionTraceTimeline = (events = []) => {
  const byStepId = {};
  events.forEach((event) => {
    byStepId[event.step_id] = event;
  });

  const resolveSeqId = (event, visited = new Set()) => {
    const direct = extractSeqId(event);
    if (direct) return direct;
    if (!event?.parent_id || visited.has(event.step_id)) return null;
    visited.add(event.step_id);
    const parent = byStepId[event.parent_id];
    if (!parent) return null;
    return resolveSeqId(parent, visited);
  };

  const timeline = [];
  let activeBlock = null;

  const flushBlock = () => {
    if (!activeBlock) return;
    timeline.push({
      type: "sequence",
      id: activeBlock.id,
      seqId: activeBlock.seqId,
      iteration: activeBlock.iteration,
      events: activeBlock.events,
    });
    activeBlock = null;
  };

  events.forEach((event) => {
    const seqId = resolveSeqId(event);

    if (!seqId) {
      flushBlock();
      timeline.push({ type: "event", event });
      return;
    }

    const iteration = getIterationForEvent(event, byStepId);
    const key = blockKey(seqId, iteration);

    if (event.event_type === "iteration_start") {
      if (activeBlock && activeBlock.key === key) {
        if (!activeBlock.events.some((e) => e.step_id === event.step_id)) {
          activeBlock.events.push(event);
        }
        return;
      }

      flushBlock();
      activeBlock = {
        key,
        id: `trace-${seqId}-iter-${iteration ?? "unknown"}`,
        seqId,
        iteration,
        events: [event],
      };
      return;
    }

    if (!activeBlock || activeBlock.key !== key) {
      flushBlock();
      activeBlock = {
        key,
        id: `trace-${seqId}-iter-${iteration ?? "unknown"}`,
        seqId,
        iteration,
        events: [],
      };
    }

    if (!activeBlock.events.some((e) => e.step_id === event.step_id)) {
      activeBlock.events.push(event);
    }
  });

  flushBlock();

  return { timeline };
};

/** @deprecated Use buildExecutionTraceTimeline — kept for tests */
export const organizeExecutionTraceEvents = (events = []) => {
  const { timeline } = buildExecutionTraceTimeline(events);
  const pipelineEvents = timeline
    .filter((item) => item.type === "event")
    .map((item) => item.event);
  const sequenceMap = new Map();

  timeline
    .filter((item) => item.type === "sequence")
    .forEach((item) => {
      if (!sequenceMap.has(item.seqId)) {
        sequenceMap.set(item.seqId, { id: item.seqId, iterations: [] });
      }
      sequenceMap.get(item.seqId).iterations.push({
        iteration: item.iteration,
        events: item.events,
      });
    });

  return {
    pipelineEvents,
    sequences: Array.from(sequenceMap.values()),
  };
};

export const getSequenceSummary = (sequenceGroup) => {
  const scores = [];
  sequenceGroup.iterations.forEach((iterationBlock) => {
    iterationBlock.events.forEach((event) => {
      if (typeof event.details?.score === "number") {
        scores.push({
          iteration: iterationBlock.iteration,
          score: event.details.score,
        });
      }
    });
  });

  return {
    iterationCount: sequenceGroup.iterations.length,
    scores,
    lastScore: scores.length > 0 ? scores[scores.length - 1].score : null,
    firstScore: scores.length > 0 ? scores[0].score : null,
  };
};
